import { Contract, PlanfactOperation, Task } from '../services/api';
import { isOperationExpense, isOperationReceipt, excludeTaxOperations, normalizeOperationAmount } from './operationBudgetArticle';
import { sumTasksNumericField } from './taskFinance';
import { formatContractRecordId } from './table';

export const PLANFACT_MARGIN_CATEGORY_EXCLUSIONS = new Set([
  'Маржа',
  'Маржа РП',
  'Наглость',
  'Маржа Акк',
]);

export function normalizeContractDealCode(value: string): string {
  return value.trim().toUpperCase();
}

/** Сделка в PlanFact соответствует договору, если равна CTR{id} или начинается с этого кода. */
export function operationDealMatchesContractId(dealName: string | undefined, contractId: number): boolean {
  if (!dealName?.trim()) {
    return false;
  }
  const normalized = normalizeContractDealCode(dealName);
  const code = normalizeContractDealCode(formatContractRecordId(contractId));
  return normalized === code || normalized.startsWith(`${code} `) || normalized.startsWith(`${code}-`);
}

export function sumContractReceiptsFromOperations(
  operations: PlanfactOperation[],
  contractId: number,
): number {
  let total = 0;
  for (const operation of excludeTaxOperations(operations)) {
    if (!isOperationReceipt(operation)) {
      continue;
    }
    if (operationDealMatchesContractId(operation.deal_name, contractId)) {
      total += normalizeOperationAmount(operation.amount, operation.operation_type);
    }
  }
  return total;
}

export function sumContractFamilyReceiptsFromOperations(
  operations: PlanfactOperation[],
  familyContractIds: Iterable<number>,
): number {
  const familyIds = new Set(familyContractIds);
  let total = 0;
  for (const operation of excludeTaxOperations(operations)) {
    if (!isOperationReceipt(operation)) {
      continue;
    }
    for (const contractId of familyIds) {
      if (operationDealMatchesContractId(operation.deal_name, contractId)) {
        total += normalizeOperationAmount(operation.amount, operation.operation_type);
        break;
      }
    }
  }
  return total;
}

function isPlanfactMarginCategory(categoryName: string | undefined): boolean {
  return categoryName != null && PLANFACT_MARGIN_CATEGORY_EXCLUSIONS.has(categoryName);
}

export function sumFamilyTaskReceiptsFromOperations(
  operations: PlanfactOperation[],
  familyTasks: Task[],
): number {
  const taskIds = new Set(familyTasks.map((task) => task.task_id.trim()).filter(Boolean));
  if (taskIds.size === 0) {
    return 0;
  }
  let total = 0;
  for (const [projectName, amount] of sumReceiptsByProjectName(operations)) {
    if (taskIds.has(projectName)) {
      total += amount;
    }
  }
  return total;
}

/** Поступления (ДОХОДЫ) из PlanFact, сгруппированные по project_name = task_id. */
export function sumReceiptsByProjectName(
  operations: PlanfactOperation[],
): Map<string, number> {
  const totals = new Map<string, number>();
  for (const operation of excludeTaxOperations(operations)) {
    if (!isOperationReceipt(operation) || !operation.project_name?.trim()) {
      continue;
    }
    const projectName = operation.project_name.trim();
    const amount = normalizeOperationAmount(operation.amount, operation.operation_type);
    totals.set(projectName, (totals.get(projectName) ?? 0) + amount);
  }
  return totals;
}

export function sumFamilyTaskActualExpensesFromOperations(
  operations: PlanfactOperation[],
  familyTasks: Task[],
): number {
  const taskIds = new Set(familyTasks.map((task) => task.task_id.trim()).filter(Boolean));
  if (taskIds.size === 0) {
    return 0;
  }
  let total = 0;
  for (const operation of operations) {
    if (!operation.project_name?.trim()) {
      continue;
    }
    const projectName = operation.project_name.trim();
    if (!taskIds.has(projectName)) {
      continue;
    }

    if (operation.tax_amount != null && operation.tax_amount !== 0) {
      total += operation.tax_amount;
    }

    if (operation.serv_fee_amount != null && operation.serv_fee_amount !== 0) {
      total += operation.serv_fee_amount;
    }

    if (operation.cash_amount != null && operation.cash_amount !== 0) {
      total += operation.cash_amount;
    }

    if (!isOperationExpense(operation)) {
      continue;
    }
    if (isPlanfactMarginCategory(operation.category_name)) {
      continue;
    }
    total += normalizeOperationAmount(operation.amount, operation.operation_type);
  }
  return total;
}

export interface FamilyFinance {
  receipts: number;
  expenses: number;
  balance: number;
  paymentPaid: number;
  hasFinanceData: boolean;
}

/** Поступления, расходы и баланс семьи — та же логика, что на карточке договора. */
export function resolveFamilyFinance(params: {
  familyContractIds: Iterable<number>;
  familyTasks: Task[];
  mainContract: Contract;
  planfactOperations?: PlanfactOperation[];
}): FamilyFinance {
  const { familyContractIds, familyTasks, mainContract, planfactOperations = [] } = params;
  const hasPlanfact = planfactOperations.length > 0;

  let receipts: number;
  let expenses: number;

  if (hasPlanfact && familyTasks.length > 0) {
    receipts = sumFamilyTaskReceiptsFromOperations(planfactOperations, familyTasks);
    expenses = sumFamilyTaskActualExpensesFromOperations(planfactOperations, familyTasks);
  } else if (
    mainContract.family_task_receipts != null ||
    mainContract.family_actual_expenses != null
  ) {
    receipts = Number(mainContract.family_task_receipts) || 0;
    expenses = Number(mainContract.family_actual_expenses) || 0;
  } else {
    receipts = sumTasksNumericField(familyTasks, 'payment_paid') ?? 0;
    expenses = sumTasksNumericField(familyTasks, 'actual_expenses') ?? 0;
  }

  const paymentPaid = hasPlanfact
    ? sumContractFamilyReceiptsFromOperations(planfactOperations, familyContractIds)
    : Number(mainContract.payment_paid) || 0;

  const hasFinanceData =
    hasPlanfact ||
    mainContract.family_task_receipts != null ||
    mainContract.family_actual_expenses != null ||
    mainContract.payment_paid != null ||
    receipts !== 0 ||
    expenses !== 0;

  return {
    receipts,
    expenses,
    balance: receipts + expenses,
    paymentPaid,
    hasFinanceData,
  };
}
